<template>
  <div
    :class="rootClasses"
    class="control"
    @click.stop
  >
    <template v-if="$slots.default">
      <label class="label">
        <span class="is-semi-bold font-sm">
          <slot />
        </span>
      </label>
    </template>

    <money
      v-if="isMoneyMask"
      v-model.lazy="internalValue"
      v-bind="moneyConfig"
      :disabled="$attrs.disabled"
      :[tabindexAttr]="tabindex"
      type="tel"
      :class="['input', { 'is-danger': errorMessage }]"
      @input="emitEvent('input', internalValue)"
      @blur="emitEvent('blur')"
      @focus="emitEvent('focus')"
    />

    <span
      v-else
      class="input-container"
    >
      <NumberControl
        v-if="hasNumberControls"
        type="decrement"
        :disabled="isDecrementDisabled"
        @click="decrement"
      />
      <input
        ref="input"
        v-bind="$attrs"
        :value="value"
        :placeholder="resolvedPlaceholder"
        :[tabindexAttr]="tabindex"
        :class="['input font-md', { 'is-danger': errorMessage }]"
        @input="handleInput"
        @blur="emitEvent('blur')"
        @focus="emitEvent('focus')"
        @change="handleChange"
      >
      <NumberControl
        v-if="hasNumberControls"
        type="increment"
        :disabled="isIncrementDisabled"
        @click="increment"
      />
    </span>

    <!-- paste -->
    <button
      v-if="showCopyIcon"
      type="button"
      class="paste-icon"
      @click.prevent="pasteFromClipboard"
    >
      <PasteFromClipboardSVG />
    </button>

    <!-- error -->
    <div v-if="errorMessage" class="error-message">
      <small class="has-text-danger">{{ errorMessage }}</small>
    </div>

    <!-- hint -->
    <slot
      v-if="hint || $slots.hint"
      name="hint"
    >
      <small>{{ hint }}</small>
    </slot>
  </div>
</template>

<script>
import PasteFromClipboardSVG from '@/assets/icons/PasteFromClipboard.vue';
import { readClipboardText, CLIPBOARD_ERRORS } from '@/utils/clipboard';
import { toast } from '@/utils/message';
import NumberControl from './NumberControl.vue';
import { applyMask, getPlaceholder, hasMask } from './masks';
import { countDigitsBeforeIndex, findIndexAfterNDigits } from './cursor';

const MONEY_CONFIG = {
  decimal: ',',
  thousands: '.',
  prefix: 'R$ ',
  precision: 2,
  masked: false,
};

const CLIPBOARD_MESSAGES = {
  [CLIPBOARD_ERRORS.UNSUPPORTED]: 'Navegador sem suporte para esta função. Use Ctrl+V.',
  [CLIPBOARD_ERRORS.DENIED]: 'Por favor permita o acesso à área de transferência.',
  [CLIPBOARD_ERRORS.EMPTY]: 'Área de transferência vazia.',
};

export default {
  name: 'BaseInput',
  components: {
    PasteFromClipboardSVG,
    NumberControl,
  },
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number],
      default: '',
    },
    tabindex: {
      type: [Number, String],
      default: null,
      required: false,
    },
    mask: {
      type: String,
      default: '',
      required: false,
    },
    errorMessage: {
      type: String,
      default: '',
      required: false,
    },
    showCopyIcon: {
      type: Boolean,
      default: false,
      required: false,
    },
    hint: {
      type: String,
      default: '',
    },
    hasNumberControls: {
      type: Boolean,
      default: false,
      required: false,
    },
    isInline: {
      type: Boolean,
      default: false,
      required: false,
    },
    full: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data: () => ({
    internalValue: '',
    moneyConfig: MONEY_CONFIG,
  }),
  computed: {
    isMoneyMask() {
      return this.mask === 'money';
    },
    tabindexAttr() {
      return this.tabindex != null ? 'tabindex' : null;
    },
    resolvedPlaceholder() {
      return this.$attrs.placeholder ?? getPlaceholder(this.mask);
    },
    rootClasses() {
      return {
        'is-line': this.isInline,
        'is-fullwidth': this.full,
        'has-number-controls': this.hasNumberControls,
      };
    },
    numberStep() {
      return this.parseNumberOr(this.$attrs.step, 1);
    },
    numberMin() {
      return this.parseNumberOr(this.$attrs.min, null);
    },
    numberMax() {
      return this.parseNumberOr(this.$attrs.max, null);
    },
    numericValue() {
      return this.parseNumberOr(this.value, 0);
    },
    isDecrementDisabled() {
      return this.numberMin !== null
        && this.numericValue - this.numberStep < this.numberMin;
    },
    isIncrementDisabled() {
      return this.numberMax !== null
        && this.numericValue + this.numberStep > this.numberMax;
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(next) {
        if (next === null) return;
        this.internalValue = next;
        this.$nextTick(this.normalizeValue);
      },
    },
  },
  methods: {
    emitEvent(name, payload = true) {
      this.$emit(name, payload);
    },
    parseNumberOr(value, fallback) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? fallback : parsed;
    },
    // Reaplica a máscara sempre que `value` mudar, seja no mount, seja
    // por atribuição programática posterior (ex.: dado assíncrono sem
    // máscara vindo da API), não só na digitação (handleInput).
    normalizeValue() {
      if (!hasMask(this.mask) || this.isMoneyMask) return;
      const masked = applyMask(this.value, this.mask);
      if (masked !== this.value) this.emitEvent('input', masked);
    },
    handleInput(event) {
      const element = event.target;
      const rawValue = element.value;

      if (!hasMask(this.mask)) {
        this.emitEvent('input', rawValue);
        return;
      }

      const cursorPosition = element.selectionStart ?? rawValue.length;
      const digitsBeforeCursor = countDigitsBeforeIndex(rawValue, cursorPosition);
      const maskedValue = applyMask(rawValue, this.mask);

      this.emitEvent('input', maskedValue);
      this.restoreCursor(element, maskedValue, digitsBeforeCursor);
    },
    handleChange(event) {
      const value = hasMask(this.mask)
        ? applyMask(event.target.value, this.mask)
        : event.target.value;
      this.emitEvent('change', value);
    },
    restoreCursor(element, maskedValue, digitsBeforeCursor) {
      this.$nextTick(() => {
        if (document.activeElement !== element) return;
        const nextCursor = findIndexAfterNDigits(maskedValue, digitsBeforeCursor);
        element.setSelectionRange(nextCursor, nextCursor);
      });
    },
    async pasteFromClipboard() {
      try {
        const text = await readClipboardText();
        const formatted = hasMask(this.mask) ? applyMask(text, this.mask) : text;
        this.emitEvent('input', formatted);
      } catch (error) {
        toast(
          CLIPBOARD_MESSAGES[error.message]
          ?? CLIPBOARD_MESSAGES[CLIPBOARD_ERRORS.UNSUPPORTED],
          'warning',
        );
      }
    },
    decrement() {
      if (this.isDecrementDisabled) return;
      this.emitEvent('input', this.numericValue - this.numberStep);
    },
    increment() {
      if (this.isIncrementDisabled) return;
      this.emitEvent('input', this.numericValue + this.numberStep);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';
@import '@/assets/scss/src/_fieldSignature.scss';

.control {
  position: relative;
  margin-bottom: 0;

  .label {
    margin-bottom: 0;

    span {
      font: {
        family: Inter;
        size: 14px;
        weight: 400;
      }
      line-height: 20px;
      color: #666;
      display: flex;
      align-items: center;
      gap: 4px;
    }
  }

  input {
    @include field-frame;
    @include field-text;
    @include field-focus;
    height: 42px;

    &.is-danger {
      border-color: #F14668;
    }
  }

  .paste-icon {
    display: inline-flex;
    position: absolute;
    right: 12px;
    margin-top: 12px;
    background: none;
    border: 0;
    padding: 0;
    cursor: pointer;
    user-select: none;
  }

  .error-message {
    min-height: 20px;
    margin-top: 5px;

    .has-text-danger {
      display: block;
      font-size: 12px;
      line-height: 16px;
      overflow-wrap: break-word;
    }
  }

  &.is-line {
    display: grid;
    grid-template-areas:
      'label input-container'
      'error-message error-message'
      'hint hint';
    column-gap: 1rem;
    width: fit-content;

    .label {
      display: flex;
      align-items: center;
    }
  }
}

.has-number-controls {
  $border-radius: 6px;

  .input-container {
    display: flex;

    > * {
      flex: 0 0 auto;
    }

    .input {
      flex: 1 1 auto;
      border-right-width: 0;
      border-left-width: 0;
      border-radius: 0;
      text-align: center;
      appearance: none;

      &[type='number'] {
        -moz-appearance: textfield;
      }

      &::-webkit-outer-spin-button,
      &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
      }
    }
  }

  .number-control {
    $bg: #FCFCFD;

    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    min-width: 38px;
    height: 42px;
    border: 1px solid #D1D5DB;
    background-color: $bg;
    color: $gray-900;
    cursor: pointer;
    user-select: none;

    &:hover:not(:disabled) { background-color: darken($bg, 5%); }
    &:active:not(:disabled) { background-color: lighten($bg, 15%); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }

    &.decrement { border-radius: $border-radius 0 0 $border-radius; }
    &.increment { border-radius: 0 $border-radius $border-radius 0; }
  }
}

.is-fullwidth {
  width: 100%;
}
</style>
